import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Grid,
	Heading,
	If,
	Modal,
	ModalProps,
	Row,
	StyleProps,
	toGrammaticalNumber,
} from "@hex-insights/core";
import { SlimEmptyView } from "../../../../../Components/Empty";
import { Tile } from "../../../../../Components/Tile";
import {
	Student,
	StudentFilterInput,
	StudentNameAndImageFragment,
	StudentNameDescriptionAndImageFragment,
	truncateDisplayItems,
	useStudentNamesAndImagesQuery,
} from "../../../../../Utilities";
import { StudentLink } from "../../../../Links";
import { StudentAdditionalCountIcon, StudentIcon } from "../../Icon";
import {
	ControlledStudentsListModal,
	ControlledStudentsListModalProps,
	StudentsListModal,
	StudentsListModalProps,
} from "../../ListModal";

export type ControlledStudentsGridTileProps = {
	filters?: StudentFilterInput;
	columns: number;
	rows: number;
} & Pick<ControlledStudentsListModalProps, "groupBy"> &
	Partial<ClassNameProps & StyleProps>;

export function ControlledStudentsGridTile({
	filters,
	groupBy,
	columns,
	rows,
	className,
	style,
}: ControlledStudentsGridTileProps) {
	const { loading, data } = useStudentNamesAndImagesQuery({
		variables: {
			filters,
			pagination: { first: columns * rows + 1 },
		},
	});
	const students = React.useMemo(() => data?.studentConnection.edges.map((e) => e.node), [data]);

	const ListModal = React.useCallback(
		({ ifRef, onClose }: Pick<ModalProps, "ifRef" | "onClose">) => (
			<ControlledStudentsListModal filters={filters} groupBy={groupBy} ifRef={ifRef} onClose={onClose} />
		),
		[filters, groupBy],
	);

	return (
		<BaseStudentsGridTile
			isLoading={loading}
			students={students}
			totalCount={data?.studentConnection.totalCount}
			columns={columns}
			rows={rows}
			ListModal={ListModal}
			className={className}
			style={style}
		/>
	);
}

export type StudentsGridTileProps = {
	isLoading?: boolean;
	students: (Pick<Student, "id"> & StudentNameDescriptionAndImageFragment)[] | null | undefined;
	columns: number;
	rows: number;
} & Pick<StudentsListModalProps, "groupBy"> &
	Partial<ClassNameProps & StyleProps>;

export function StudentsGridTile({
	isLoading,
	students,
	groupBy,
	columns,
	rows,
	className,
	style,
}: StudentsGridTileProps) {
	const ListModal = React.useCallback(
		({ ifRef, onClose }: Pick<ModalProps, "ifRef" | "onClose">) => (
			<StudentsListModal
				isLoading={isLoading}
				students={students ?? []}
				groupBy={groupBy}
				ifRef={ifRef}
				onClose={onClose}
			/>
		),
		[isLoading, students, groupBy],
	);

	return (
		<BaseStudentsGridTile
			isLoading={isLoading}
			students={students}
			columns={columns}
			rows={rows}
			ListModal={ListModal}
			className={className}
			style={style}
		/>
	);
}

type BaseStudentsGridTileProps = {
	isLoading?: boolean;
	students: (Pick<Student, "id"> & StudentNameAndImageFragment)[] | null | undefined;
	totalCount?: number;
	columns: number;
	rows: number;
	ListModal: (props: Pick<ModalProps, "ifRef" | "onClose">) => JSX.Element | null;
} & Partial<ClassNameProps & StyleProps>;

function BaseStudentsGridTile({
	isLoading = false,
	students,
	totalCount,
	columns,
	rows,
	ListModal,
	className,
	style,
}: BaseStudentsGridTileProps) {
	const total = totalCount ?? students?.length ?? 0;

	const { displayedItems, anyHidden } = truncateDisplayItems(students ?? [], columns * rows);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Tile className={className} style={style}>
				<Tile.Header>
					<Row justify="space-between" align="center">
						<Heading.H2 noMargin>{toGrammaticalNumber("Student", total, total > 0)}</Heading.H2>

						<If condition={total > 0}>
							<Button variant="link" size="small" onClick={openModal}>
								View All
							</Button>
						</If>
					</Row>
				</Tile.Header>
				<Tile.Body>
					<Conditional>
						<If condition={isLoading && !students}>Loading...</If>
						<If condition={!!students && students.length === 0}>
							<SlimEmptyView>No students listed</SlimEmptyView>
						</If>
						<Else>
							<Grid tracks={columns} gap="0.75rem">
								{displayedItems.map((student) => (
									<StudentLink key={student.id} instance={student} className="link--no-text-decoration">
										<Column justify="spaced-start" align="center" verticalSpacing="0.25rem">
											<StudentIcon student={student} size={36} />
										</Column>
									</StudentLink>
								))}
								<If condition={anyHidden}>
									<StudentAdditionalCountIcon count={total - displayedItems.length} size={36} />
								</If>
							</Grid>
						</Else>
					</Conditional>
				</Tile.Body>
			</Tile>

			<Modal.If condition={isModalOpen}>
				<ListModal onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}
